"use client";

import { useState, useRef, useEffect } from 'react';
import { GetUserProfileImage } from '@/app/dashboard/chat-bot/actions';
import ChatBotUi from './chatBotUi';
import { speakResponse } from "./chatBotUtils";
import ChatBotToggleButton from './ChatBotToggleButton';
import { usePathname } from 'next/navigation';
import { fetchLocation } from './actions';
import HttpStatus from '@/lib/HttpStatus';

interface Message {
  role: 'user' | 'agent';
  text: string;
}

export default function FloatingChat() {
  // if this page is chat-bot page, no need to display the floating chat-bot...
  const pathname = usePathname();
  if(pathname.endsWith("/chat-bot")) return null;

  const [isOpen, setIsOpen] = useState(false);
  const [userLocation, setUserLocation] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);


  const toggleListening = () => {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      
      if (!SpeechRecognition) {
      alert("Browser does not support Speech Recognition.");
      return;
      }

      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.continuous = false;

      if (isListening) {
      recognition.stop();
      return;
      }

      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognition.onerror = () => setIsListening(false);

      recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      // Immediately trigger the send logic with the transcript
      handleSend(transcript);
      };

      recognition.start();
  };

  // --- CORE LOGIC ---
  useEffect(() => {
      const fetchUserPhoto = async () => {
        try {
            const photo = await GetUserProfileImage();
            setUserPhoto(photo);
        } catch (error) {
            console.error("Failed to fetch user photo:", error);
        }
      };
      
      // getCity();
      fetchUserPhoto();

      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const { latitude, longitude } = position.coords;
          console.log({ latitude, longitude});
          try {
            // // We use a free Reverse Geocoding API to turn coordinates into a Suburb name
            const res = await fetchLocation(latitude, longitude);
            if(res.status != HttpStatus.OK) throw new Error(res.error!);
            
            const specificLocation = res.data;
            console.log("specificLocation: ", specificLocation);
            setUserLocation(specificLocation);
          } catch (error) {
            console.error("Reverse geocoding failed", error);
          }
        }, (error) => {
          console.error("User denied location or error occurred", error);
        }, {
          enableHighAccuracy: true // This forces the device to use GPS if available
        });
      }
  }, []);

  useEffect(() => {
      const textarea = textareaRef.current;
      if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
      if (document.activeElement === textarea) {
          textarea.focus({ preventScroll: true });
      }
      }
  }, [input]);

  useEffect(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, isLoading]);

  const handleSend = async (overrideInput?: string) => {
      const textToSend = overrideInput || input;
      if (!textToSend.trim() || isLoading) return;

      const userMessage: Message = { role: 'user', text: textToSend };
      setMessages((prev) => [...prev, userMessage]);
      setInput('');
      setIsLoading(true);

      try {
      const response = await fetch('/api/agent-api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            message: textToSend,
            location: userLocation 
          }),
      });

      const data = await response.json();
      const agentText = data.queryResult?.responseMessages?.[0]?.text?.text?.[0] 
                          || "I'm sorry, I couldn't process that.";

      setMessages((prev) => [...prev, { role: 'agent', text: agentText }]);
      
      // ✅ Speak the reply
      speakResponse(agentText);

      } catch (error) {
      setMessages((prev) => [...prev, { role: 'agent', text: "Error: Could not reach the AI agent." }]);
      } finally {
      setIsLoading(false);
      }
  };

  return (
    <div className="fixed bottom-6 right-6 z-30 flex flex-col items-end">
      <ChatBotUi 
        isOpen={isOpen} isLoading={isLoading} isListening={false} 
        messages={messages} userPhoto={userPhoto ?? ''} input={input} 
        handleSend={handleSend} 
        setIsOpen={setIsOpen} 
        setInput={setInput} 
        toggleListening={toggleListening} />

      {/* Toggle Button */}
      <ChatBotToggleButton isOpen={isOpen} setIsOpen={setIsOpen} />
    </div>
  );
}